import { useEffect, useMemo, useRef, useState } from 'react';
import type { Session, SessionState } from '../session/Session';
import type { TableModel } from '../model/table';
import { errorText } from '../protocol/wire';
import {
  answer, back, canGoBack, choiceEligible, currentStep, groupChoices, isComplete, sameChoiceDescriptor, setParameter, startDraft,
  type ChoiceView, type Draft, type StepValue,
} from '../model/choices';
import { usePresentation, type PickTargets } from '../presentation/PresentationContext';
import { StepControl, ParameterForm, describeAnswer } from './StepControls';
import { SelectionReview } from './SelectionReview';
import { useDraftContext } from '../play/draftContext';

interface Props {
  session: Session;
  state: SessionState;
  table: TableModel;
  choices: ChoiceView[];
  isMyTurn: boolean;
  draftChoiceId: string | null;
  onDraft: (id: string | null) => void;
  onInspect: (id: string) => void;
  onPickTargets: (targets: PickTargets | null) => void;
}

export function ChoicePanel({ session, state, table, choices, isMyTurn, draftChoiceId, onDraft, onInspect, onPickTargets }: Props) {
  const context = useDraftContext({ session, gameId: state.gameId, matchId: state.matchId, seat: state.seat });
  return <CurrentChoicePanel key={context} {...{ session, state, table, choices, isMyTurn, draftChoiceId, onDraft, onInspect, onPickTargets }} />;
}

function CurrentChoicePanel({ session, state, table, choices, isMyTurn, draftChoiceId, onDraft, onInspect, onPickTargets }: Props) {
  const api = usePresentation();
  const [filter, setFilter] = useState('');
  const available = choices.filter(choice => choiceEligible(table, choice));
  const draftChoice = draftChoiceId ? available.find(c => c.id === draftChoiceId) ?? null : null;
  const actorLabel = table.seats.find(s => s.id === table.actor)?.label ?? table.actor;
  const descriptor = useRef({ choice: draftChoice, revision: state.revision, decisionId: state.decisionId, epoch: 0 });
  if (!sameChoiceDescriptor(descriptor.current.choice, draftChoice) || draftChoice?.scope !== 'auxiliary' && (descriptor.current.revision !== state.revision || descriptor.current.decisionId !== state.decisionId)) {
    descriptor.current = { choice: draftChoice, revision: state.revision, decisionId: state.decisionId, epoch: descriptor.current.epoch + 1 };
  }

  // Quick submit (presentation contract 5.1): a stepless, unconfirmed choice submits at once.
  const pick = (c: ChoiceView) => {
    if (!isMyTurn || state.paused || state.connection !== 'open' || !choiceEligible(table, c)) return;
    session.invalidateChoiceCheck();
    if (api.quickSubmit && c.steps.length === 0 && !c.confirm && c.parameters.length === 0) void session.submitDraft(startDraft(c), c.label);
    else onDraft(c.id);
  };

  let body: React.ReactNode;
  if (state.paused) body = <p className="muted">This game is paused. Its position and decisions are saved.</p>;
  else if (table.finished) body = <p className="muted">The game is over.</p>;
  else if (!table.selfSeat) body = <p className="muted">You are watching. {actorLabel ? `${actorLabel} is taking a turn.` : ''}</p>;
  else if (state.pending) body = <p className="waiting"><span className="pulse" aria-hidden="true" />Sending: {state.pending.label}</p>;
  else if (draftChoice) body = (
    <DraftView key={descriptor.current.epoch} choice={draftChoice} session={session} canSubmit={isMyTurn && state.connection === 'open' && !state.paused} connected={state.connection === 'open'} checking={state.queryCheck?.status === 'pending'} onCancel={() => { session.invalidateChoiceCheck(); onDraft(null); onPickTargets(null); }} onInspect={onInspect}
      onPickTargets={onPickTargets} onSubmit={async draft => { if (await session.submitDraft(draft)) { onDraft(null); onPickTargets(null); } }} />
  );
  else if (state.connection !== 'open') body = <p className="muted" role="status">Reconnecting. Moves are disabled until connected.</p>;
  else if (!isMyTurn) body = <p className="waiting"><span className="pulse" aria-hidden="true" />Waiting for {actorLabel ?? 'the next player'}…</p>;
  else if (available.length === 0) body = <p className="muted">No moves are available. Waiting for the game to continue.</p>;
  else body = <ChoiceMenu choices={available} filter={filter} onFilter={setFilter} onPick={pick} />;

  return (
    <section className={`panel choices${isMyTurn ? ' is-my-turn' : ''}`} aria-label="Your moves">
      <h2 className="panel-title">{isMyTurn ? 'Your move' : 'Moves'}</h2>
      {state.notice && (
        <div className="notice" role="alert">
          <span>{state.notice.text || errorText(state.notice.code)}</span>
          <button type="button" className="icon-button" aria-label="Dismiss" onClick={() => session.dismissNotice()}>×</button>
        </div>
      )}
      {state.queryCheck && <p className="field-hint" role="status">{state.queryCheck.status === 'pending' ? 'Checking move…' : state.queryCheck.reason}</p>}
      {body}
    </section>
  );
}

function ChoiceMenu({ choices, filter, onFilter, onPick }: { choices: ChoiceView[]; filter: string; onFilter: (v: string) => void; onPick: (c: ChoiceView) => void }) {
  const api = usePresentation();
  const menu = useRef<HTMLDivElement | null>(null);
  const describe = api.describeSubject;
  const detailOf = (c: ChoiceView) => (describe && c.subjects.length === 1 ? describe(c.subjects[0]!) : null);
  const needle = filter.trim().toLowerCase();
  const visible = needle ? choices.filter(c => `${c.group} ${c.label}`.toLowerCase().includes(needle)) : choices;
  const groups = useMemo(() => groupChoices(visible).map(g => ({ ...g, ...api.groupOrder(g.group) })).sort((a, b) => a.order - b.order), [visible, api]);

  // Group hotkeys focus the first move of that group.
  useEffect(() => {
    const keyed = groups.filter(g => g.hotkey);
    if (keyed.length === 0) return;
    const listener = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey || (e.target as HTMLElement)?.closest('input, textarea, select')) return;
      const g = keyed.find(x => x.hotkey === e.key.toLowerCase());
      if (!g) return;
      e.preventDefault();
      menu.current?.querySelector<HTMLButtonElement>(`[data-group="${CSS.escape(g.group)}"] .choice-button`)?.focus();
    };
    window.addEventListener('keydown', listener);
    return () => window.removeEventListener('keydown', listener);
  }, [groups]);

  return (
    <div className="choice-menu" ref={menu}>
      {choices.length > 8 && (
        <input type="search" className="input" placeholder={`Filter ${choices.length} moves`} aria-label="Filter moves"
          value={filter} onChange={e => onFilter(e.target.value)} />
      )}
      {groups.length === 0 && <p className="muted">No moves match.</p>}
      {groups.map(g => (
        <details key={g.group} className="choice-group" open data-group={g.group}>
          <summary>{g.group}<span className="zone-count">{g.choices.length}</span>{g.hotkey && <kbd className="hotkey" title={`Press ${g.hotkey.toUpperCase()}`}>{g.hotkey.toUpperCase()}</kbd>}</summary>
          <ul className="choice-list">
            {g.choices.map(c => (
              <li key={c.id}>
                <button type="button" className="choice-button" onClick={() => onPick(c)} data-choice-id={c.id}>
                  {c.label}
                  {detailOf(c) && <small className="choice-detail">{detailOf(c)}</small>}
                </button>
              </li>
            ))}
          </ul>
        </details>
      ))}
    </div>
  );
}

interface DraftProps {
  choice: ChoiceView;
  session: Session;
  checking?: boolean;
  canSubmit: boolean;
  connected: boolean;
  onCancel: () => void;
  onSubmit: (draft: Draft) => void;
  onInspect: (id: string) => void;
  onPickTargets: (targets: PickTargets | null) => void;
}

function DraftView({ choice, session, checking, canSubmit, connected, onCancel, onSubmit, onInspect, onPickTargets }: DraftProps) {
  useEffect(() => () => session.invalidateChoiceCheck(), [session]);
  const [draft, setDraft] = useState<Draft>(() => startDraft(choice));
  const [problem, setProblem] = useState<string | null>(null);
  const [interrupted, setInterrupted] = useState(false);
  useEffect(() => { if (!connected) setInterrupted(true); }, [connected]);
  const confirmRef = useRef<HTMLButtonElement | null>(null);
  const step = currentStep(draft);
  const complete = isComplete(draft);
  useEffect(() => { if (complete) confirmRef.current?.focus(); }, [complete]);

  const submit = (value: StepValue) => {
    session.invalidateChoiceCheck();
    try { setDraft(d => answer(d, value)); setProblem(null); setInterrupted(false); } catch (e) { setProblem((e as Error).message); }
  };

  // A single pick whose options name visible objects can also be answered on the table.
  useEffect(() => {
    if (step?.type === 'pick' && (step.max ?? 1) === 1 && !step.ordered && step.options.some(o => o.objectId)) {
      const values = new Map<string, string | number | boolean>();
      for (const o of step.options) if (o.objectId) values.set(o.objectId, o.value);
      onPickTargets({ values, answer: v => submit(v) });
    } else onPickTargets(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  return (
    <div className="draft" onKeyDown={e => { if (e.key === 'Escape') onCancel(); }}>
      <p className="draft-title">{choice.label}</p>
      {choice.prompt && <p className="muted">{choice.prompt}</p>}
      <SelectionReview rows={choice.review} />
      {(!connected || interrupted) && <p className="field-hint" role="status">{connected ? 'Connection restored. Your selections are retained; review and confirm again.' : 'Connection interrupted. Your draft is retained; retry when connected.'}</p>}
      {draft.answers.length > 0 && (
        <ol className="draft-answers">
          {draft.answers.map((a, i) => <li key={i}><span className="muted">{a.step.label}:</span> {describeAnswer(a.step, a.value)}</li>)}
        </ol>
      )}
      {step && <StepControl key={draft.answers.length} step={step} group={choice.group} onAnswer={submit} onInspect={onInspect} />}
      {problem && <p className="field-error" role="alert">{problem}</p>}
      {complete && choice.parameters.length > 0 && (
        <ParameterForm fields={choice.parameters} values={draft.parameterValues} onChange={(n, v) => { session.invalidateChoiceCheck(); setInterrupted(false); setDraft(setParameter(draft, n, v)); }} />
      )}
      {complete && choice.confirm && <p className="confirm-warning" role="note">{choice.confirm}</p>}
      <div className="draft-actions">
        {complete && (
          <button ref={confirmRef} type="button" className="button button-primary" disabled={checking || !canSubmit} onClick={() => { if (canSubmit) { setInterrupted(false); onSubmit(draft); } }}>Confirm move</button>
        )}
        {canGoBack(draft) && <button type="button" className="button" onClick={() => { session.invalidateChoiceCheck(); setInterrupted(false); setDraft(back(draft)); }}>Back</button>}
        <button type="button" className="button button-quiet" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}
